import { Trans, useLingui } from '@lingui/react/macro';

import { ActivityOfType } from '@/components/activities/Activity';
import Loader from '@/components/Loader';
import config from '@/config';
import Button from '@/design_system/Button/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconInfo from '@/icons/Info.svg';
import IconUser from '@/icons/User.svg';
import IconWorkshop from '@/icons/Workshop.svg';
import {
  ArticleActivityOfType,
  useIsUpdatingServiceChoiceOrWorkshop,
  useUpdateServiceChoice,
} from '@/models/article';
import { useGetWorkshopRegion, WorkshopFromGetMany } from '@/models/workshop';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { createBEMClasses } from '@/utils/classname';

import ContactDetails from '../ContactDetails';
import { PriceWithDetailsTooltipWithConversions } from '../PriceWithDetails/PriceWithDetails';
import WorkshopPriceLabel from '../WorkshopPriceLabel/WorkshopPriceLabel';

import { AutoAcceptBadge, usePreventAutoAcceptDispatch } from './Badges/AutoAcceptBadge';
import { CancellationFeeBadge } from './Badges/CancellationFeeBadge';
import { NonDigitalizedBadge } from './Badges/NonDigitalizedBadge';
import { OfficialBadge } from './Badges/OfficialBadge';
import { WorkshopListItemWarningsBanner } from './WorkshopListItemWarningsBanner';

import './WorkshopList.css';

type WorkshopListProps = {
  selected?: string;
  workshops: WorkshopFromGetMany[];
  jobRefusedActivities: ArticleActivityOfType<'job_refused'>[];
  isFetchingWorkshops?: boolean;
};

const { block, element } = createBEMClasses('workshop-list');

const WorkshopList = ({
  workshops,
  jobRefusedActivities,
  selected,
  isFetchingWorkshops,
}: WorkshopListProps) => {
  const { t } = useLingui();
  const getWorkshopRegion = useGetWorkshopRegion();

  const workshopsByRegion = workshops.reduce<Record<string, WorkshopFromGetMany[]>>(
    (result, workshop) => {
      result[getWorkshopRegion(workshop)] ??= [];
      result[getWorkshopRegion(workshop)].push(workshop);

      return result;
    },
    {}
  );

  return (
    <Stack gap="8px" className={block({}, 'inner-shadow-on-scroll')}>
      {Object.entries(workshopsByRegion)
        .sort(([regionA], [regionB]) => regionA.localeCompare(regionB))
        .map(([region, workshops]) => (
          <Stack
            key={region}
            gap="8px"
            ariaLabel={t({ id: 'workshop-list.region.label', message: `Workshops in ${region}` })}
          >
            <Paragraph weight="regular">{region}</Paragraph>
            {workshops.map((workshop) => {
              const jobRefusedActivity = jobRefusedActivities.find(
                (activity) => activity.workshopId === workshop.id
              );

              return (
                <WorkshopListItem
                  key={workshop.id}
                  workshop={workshop}
                  jobRefusedActivity={jobRefusedActivity}
                  isFetchingWorkshops={isFetchingWorkshops}
                  selected={selected}
                />
              );
            })}
          </Stack>
        ))}
    </Stack>
  );
};

export const WorkshopListItem = ({
  workshop,
  jobRefusedActivity,
  isDisabled,
  isFetchingWorkshops,
  selected,
}: {
  workshop: WorkshopFromGetMany;
  jobRefusedActivity?: ActivityOfType<'job_refused'>;
  selected?: string;
  isFetchingWorkshops?: boolean;
  isDisabled?: boolean;
}) => {
  const { t } = useLingui();
  const { article, request } = useArticleContext();
  const isUpdatingServiceChoiceOrWorkshop = useIsUpdatingServiceChoiceOrWorkshop(article.id);

  const { mutateAsync: updateServiceChoice, isPending: isUpdateWorkshopPending } =
    useUpdateServiceChoice({
      requestId: request.id,
      articleId: article.id,
    });

  const onSelectWorkshop = (workshopId: string) => {
    updateServiceChoice({
      workshopId,
    });
  };

  const selectedLabel = t({
    id: 'components.workshop-list.workshop-selected',
    message: `Workshop ${workshop.name} selected`,
  });

  if (isDisabled) {
    return (
      <div className={element('item')} aria-label={selectedLabel}>
        <BaseWorkshopListItem
          workshop={workshop}
          jobRefusedActivity={jobRefusedActivity}
          isDisabled={isDisabled}
          isUpdateWorkshopPending={isUpdateWorkshopPending}
          isFetchingWorkshops={isFetchingWorkshops}
        />
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={element('item', {
        selected: workshop.id === selected,
        'is-loading': isUpdatingServiceChoiceOrWorkshop,
      })}
      aria-label={
        workshop.id === selected
          ? selectedLabel
          : t({
              id: 'components.workshop-list.select-workshop',
              message: `Select workshop ${workshop.name}`,
            })
      }
      onClick={() => {
        if (!isUpdateWorkshopPending) {
          onSelectWorkshop?.(workshop.id);
        }
      }}
      onKeyUp={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onSelectWorkshop?.(workshop.id);
        }
      }}
    >
      <BaseWorkshopListItem
        workshop={workshop}
        jobRefusedActivity={jobRefusedActivity}
        isDisabled={isDisabled}
        isUpdateWorkshopPending={isUpdateWorkshopPending}
        isFetchingWorkshops={isFetchingWorkshops}
      />
    </div>
  );
};

const BaseWorkshopListItem = ({
  workshop,
  jobRefusedActivity,
  isDisabled,
  isUpdateWorkshopPending,
  isFetchingWorkshops,
}: {
  workshop: WorkshopFromGetMany;
  jobRefusedActivity?: ActivityOfType<'job_refused'>;
  isFetchingWorkshops?: boolean;
  isUpdateWorkshopPending: boolean;
  isDisabled?: boolean;
}) => {
  return (
    <>
      <WorkshopListItemWarningsBanner
        workshop={workshop}
        jobRefusedActivity={jobRefusedActivity}
        isDisabled={isDisabled}
      />
      <div className={element('item__content')}>
        <object
          data={`${config.apiUrl}/entities/${workshop?.id}/logo?type=square`}
          type="image/jpeg"
        >
          <img src="/workshop.svg" alt={workshop?.name} height="24" width="24" />
        </object>
        <div className={element('item__header')}>
          <div className={element('item__header__title')}>
            <Stack row gap="0.25rem" flexWrap="nowrap" style={{ overflow: 'hidden' }}>
              <Stack
                row
                gap="0.5em"
                alignItems="center"
                style={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {(isFetchingWorkshops || isUpdateWorkshopPending) && (
                  <Loader style={{ fontSize: '0.75rem' }} />
                )}

                {workshop.name}
              </Stack>

              <WorkshopContactDetailsTooltip workshop={workshop} />
            </Stack>

            <Badges workshop={workshop} isDisabled={isDisabled} />
          </div>

          <Stack gap="0.25rem">
            <Stack gap="0.125rem">
              <WorkshopOrganizationPrice workshop={workshop} />
              <WorkshopOrganizationPriceIncludingOptionalActions workshop={workshop} />
            </Stack>

            <Stack gap="0.125rem">
              <WorkshopPrice workshop={workshop} />
              <WorkshopPriceIncludingOptionalActions workshop={workshop} />
            </Stack>
          </Stack>
        </div>
      </div>
    </>
  );
};

const Badges = ({
  workshop,
  isDisabled,
}: {
  workshop: WorkshopFromGetMany;
  isDisabled?: boolean;
}) => {
  const autoAcceptDispatchPrevented = usePreventAutoAcceptDispatch();
  const cancellationFeeAgreementPrice = workshop.feeAgreementPrices?.find(
    (agreementPrice) => agreementPrice.feeType.slug === 'cancellation'
  );

  return (
    <Stack row gap="0.5em" flexWrap="nowrap">
      {workshop.officialForBrand && <OfficialBadge />}

      {workshop.workshopConfig?.nonDigitalized ? <NonDigitalizedBadge /> : null}

      {workshop.entityOrganizations[0]?.workshopConfig?.autoAcceptDispatch &&
      !autoAcceptDispatchPrevented &&
      !isDisabled ? (
        <AutoAcceptBadge />
      ) : null}

      {!!cancellationFeeAgreementPrice?.amount &&
        !!cancellationFeeAgreementPrice?.amountBeforeTaxes && (
          <CancellationFeeBadge
            key="cancellation-fee"
            cancellationFeeAgreementPrice={cancellationFeeAgreementPrice}
          />
        )}
    </Stack>
  );
};

const WorkshopContactDetailsTooltip = ({ workshop }: { workshop: WorkshopFromGetMany }) => {
  return (
    <Tooltip
      content={
        <ContactDetails
          address={workshop.address}
          email={workshop.address?.contactEmail}
          phone={workshop.contactInfo?.phone}
          size="small"
          placeIcon={null}
          addressFirst
        />
      }
      variant="light"
      textAlign="start"
      placement="bottom"
      size="medium"
    >
      <Button variant="style-less">
        <IconInfo style={{ fontSize: '1rem' }} className="text-disabled" />
      </Button>
    </Tooltip>
  );
};

const WorkshopOrganizationPrice = ({ workshop }: { workshop: WorkshopFromGetMany }) => {
  const { view } = useRequestContext();

  const organizationPrice =
    workshop.organizationPriceExcludingOptionalActions === undefined
      ? workshop.organizationPrice
      : workshop.organizationPriceExcludingOptionalActions;

  if (!view.organizationPrice.shown || organizationPrice === undefined) {
    return null;
  }

  return (
    <Stack row alignItems="center" gap="0.25rem">
      <Tooltip content={view.organizationPrice.labelWithoutVATInfo}>
        <Button variant="style-less" className={element('price-icon-button')}>
          <IconUser className="text-secondary" style={{ fontSize: '1rem' }} />
        </Button>
      </Tooltip>

      {organizationPrice === null ? (
        <InlineText size="100" weight="medium">
          <Trans id="article.price.pending-quote">Pending quote</Trans>
        </InlineText>
      ) : (
        <>
          <PriceWithDetailsTooltipWithConversions
            price={organizationPrice}
            options={{
              showAmountBeforeTaxes: view.organizationPrice.showAmountBeforeTaxes,
            }}
            size="medium"
            tooltipDirection="row-reverse"
          />
          <InlineText size="200" color="secondary">
            (
            {view.organizationPrice.showAmountBeforeTaxes ? (
              <Trans id="components.workshop-list.organization-price.excl-vat">excl. VAT</Trans>
            ) : (
              <Trans id="components.workshop-list.organization-price.incl-vat">incl. VAT</Trans>
            )}
            )
          </InlineText>
        </>
      )}
    </Stack>
  );
};

const WorkshopOrganizationPriceIncludingOptionalActions = ({
  workshop,
}: {
  workshop: WorkshopFromGetMany;
}) => {
  const { view } = useRequestContext();

  if (
    !view.organizationPrice.shown ||
    workshop.organizationPrice === undefined ||
    workshop.organizationPriceExcludingOptionalActions === undefined ||
    workshop.organizationPrice?.amount ===
      workshop.organizationPriceExcludingOptionalActions?.amount
  ) {
    return null;
  }

  return (
    <Stack row alignItems="center" gap="0.25rem" style={{ marginLeft: '1.25rem' }}>
      {workshop.organizationPrice === null ? (
        <InlineText size="200" weight="medium">
          <Trans id="article.price.pending-quote">Pending quote</Trans>
        </InlineText>
      ) : (
        <PriceWithDetailsTooltipWithConversions
          price={workshop.organizationPrice}
          options={{
            showAmountBeforeTaxes: view.organizationPrice.showAmountBeforeTaxes,
          }}
          size="x-small"
          showConversionTooltip={false}
        />
      )}
      <InlineText size="200" color="secondary">
        <Trans id="components.workshop-list.organization-price.incl-optional-actions">
          incl. optional actions
        </Trans>
      </InlineText>
    </Stack>
  );
};

const WorkshopPrice = ({ workshop }: { workshop: WorkshopFromGetMany }) => {
  const { view } = useRequestContext();

  const workshopPrice =
    workshop.workshopPriceExcludingOptionalActions === undefined
      ? workshop.workshopPrice
      : workshop.workshopPriceExcludingOptionalActions;

  if (!view.workshopPrice.shown || workshopPrice === undefined) {
    return null;
  }

  return (
    <Stack row alignItems="center" gap="0.25rem">
      <Tooltip content={view.workshopPrice.labelWithoutVATInfo}>
        <Button variant="style-less" className={element('price-icon-button')}>
          <IconWorkshop className="text-secondary" style={{ fontSize: '1rem' }} />
        </Button>
      </Tooltip>

      <Stack row alignItems="baseline" gap="0.25rem">
        {workshopPrice === null ? (
          <InlineText size="100" weight="medium">
            <Trans id="article.price.pending-quote">Pending quote</Trans>
          </InlineText>
        ) : (
          <>
            <PriceWithDetailsTooltipWithConversions
              price={workshopPrice}
              options={{
                showAmountBeforeTaxes: view.workshopPrice.showAmountBeforeTaxes,
              }}
              size="medium"
              tooltipDirection="row-reverse"
            />
            <InlineText size="200">
              <WorkshopPriceLabel
                noVATApplied={workshop.workshopPrice?.noVATApplied}
                size="200"
                hideLabel
              />
            </InlineText>
          </>
        )}
      </Stack>
    </Stack>
  );
};

const WorkshopPriceIncludingOptionalActions = ({ workshop }: { workshop: WorkshopFromGetMany }) => {
  const { view } = useRequestContext();

  if (
    !view.workshopPrice.shown ||
    workshop.workshopPrice === undefined ||
    workshop.workshopPriceExcludingOptionalActions === undefined ||
    workshop.workshopPrice?.amount === workshop.workshopPriceExcludingOptionalActions?.amount
  ) {
    return null;
  }

  return (
    <Stack row alignItems="center" gap="0.25rem" style={{ marginLeft: '1.25rem' }}>
      {workshop.workshopPrice === null ? (
        <InlineText size="200" weight="medium">
          <Trans id="article.price.pending-quote">Pending quote</Trans>
        </InlineText>
      ) : (
        <PriceWithDetailsTooltipWithConversions
          price={workshop.workshopPrice}
          options={{
            showAmountBeforeTaxes: view.workshopPrice.showAmountBeforeTaxes,
          }}
          size="x-small"
          showConversionTooltip={false}
        />
      )}
      <InlineText size="200" color="secondary">
        <Trans id="components.workshop-list.workshop-price.incl-optional-actions">
          incl. optional actions
        </Trans>
      </InlineText>
    </Stack>
  );
};

export default WorkshopList;
